import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { formatDateAndTime } from 'utilities/time';
import { HistoryFilter } from 'components/History';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import { SparkDataTable } from 'sharedComponents/UI';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import { PaginationQuery } from 'utilities/hooks/urlGeneration';
import Pagination from 'sharedComponents/Pagination';

import BoardCollaboratorsPopup from './BoardCollaboratorsPopup';
import BoardOptionsMenu from 'components/common/SparkOutputBoard/BoardToolBar/BoardOptionsMenu';
import { useNavigate } from 'react-router-dom';
import type { BoardsResponse } from 'types/Boards';
import { WorkSpaceIcon } from 'components/SVG';

type BoardListViewProps = {
  isParentLoading?: boolean;
  isParentFetching?: boolean;
  sparkID?: string;
  historyFilter?: HistoryFilter;
  selectedStatus?: string;
  showPaginationControls?: boolean;
  paginatedQuery?: PaginationQuery;
  total: number;
  currentPagination: PaginationQuery;
  boards: BoardsResponse | undefined;
  setCurrentPagination: React.Dispatch<React.SetStateAction<PaginationQuery>>;
};

type BoardListViewRow = {
  id: string;
  name: string;
  modified: string;
  collaborators: string;
  imageUrl: string;
};

function BoardsListView({
  showPaginationControls = false,
  paginatedQuery,
  currentPagination,
  boards,
  total,
  setCurrentPagination,
}: BoardListViewProps) {
  const navigate = useNavigate();

  const getPagination = () => {
    if (showPaginationControls) {
      return currentPagination;
    }
    return !!paginatedQuery ? paginatedQuery : undefined;
  };

  const columns: Column<BoardListViewRow>[] = [
    {
      field: 'name',
      headerText: 'Name',
      flex: 3,
      sortable: true,
      cellRenderer: (row) => {
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <SparkAvatarBackground
              src={row.imageUrl || ''}
              icon={<WorkSpaceIcon />}
              width='32px'
              height='32px'
            />

            <Box>
              <Typography
                variant='body1'
                sx={{
                  color: '#000000',
                }}
              >
                {row.name || '- -'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'collaborators',
      headerText: 'Members',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <Box textAlign='center'>
              <Grid item>
                <BoardCollaboratorsPopup resourceID={row.id} />
              </Grid>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'modified',
      headerText: 'Modified',
      filterAble: true,
      sortable: true,
      flex: 1,
      cellRenderer: (row) => {
        return (
          <Typography variant='body2' sx={{ color: 'grey.600', pt: 0.5 }}>
            {formatDateAndTime(row.modified || '')}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerText: '',
      flex: 1,
      justifyContent: 'flex-end',
      cellRenderer: (row) => {
        return (
          <Box
            display='flex'
            alignItems='center'
            gap={2}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
            }}
          >
            <BoardOptionsMenu
              boardID={row.id || ''}
              shouldRenderShareButton={true}
            />
          </Box>
        );
      },
    },
  ];

  const rows: BoardListViewRow[] | undefined = boards?.data?.map((board) => ({
    id: board?.id || '',
    name: board?.attributes?.name || '',
    modified: board?.attributes?.modifiedAt || '',
    collaborators: board?.attributes?.emoji || '',
    imageUrl: board?.attributes?.emoji || '',
  }));

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <SparkDataTable<BoardListViewRow>
        columns={columns}
        rows={rows || []}
        onRowClick={(rowID) => navigate(`/boards/${rowID}`)}
        pagination={false}
        shouldRenderHeader={true}
        showLeftChevron={false}
        defaultSortOrder={{
          field: 'modified',
          dir: 'desc'
        }}
      />
      {showPaginationControls && (
        <Pagination
          itemsPerPage={getPagination()?.pageSize || 0}
          dataCount={total}
          currentPage={getPagination()?.pageNumber || 1}
          setCurrentPage={(pageNumber) =>
            setCurrentPagination((prev) => ({
              ...prev,
              pageNumber: pageNumber,
            }))
          }
          gridProps={{ marginTop: 1 }}
        />
      )}
    </Box>
  );
}

export default BoardsListView;
